import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
// components
import TestHistoryResult from '../../moleculs/testHistoryResult';
import EmptyList from '../../atoms/emptyList';
import PageLoader from '../../shared/pageLoader';
// actions
import resultsAction from '../../../redux/actions/results';
// constants
import { TEST_HISTORY_PAGE_SIZE, TEST_HISTORY_SCROLL_SLACK_PX } from '../../../constants/testHistoryPagination';
// tools
import { hasRenderableTestResult } from '../../../tools';
// localization
import strings from '../../../localization';
// types
import { loadingType } from '../../../types/types';
import { resultsResponseType } from '../../../types/results';
// style
import './index.scss';

interface TestHistoryProps {
  patientId: number;
  results: resultsResponseType;
  unitMeasure: string;
  loading: loadingType;
  disabled: boolean;
  userType: string;
}

const TestHistory: FC<TestHistoryProps> = ({
  patientId,
  results,
  unitMeasure,
  loading,
  disabled,
  userType,
}: TestHistoryProps) => {
  const [isResultsReady, setIsResultsReady] = useState<boolean>(false);
  const resultsFetchStartedRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const visibleTestResults = results.testResultList.filter((item) => hasRenderableTestResult(item.testResult));
  const hasMoreResults = results.testCounter > results.testResultList.length;

  useEffect(() => {
    return () => {
      dispatch(resultsAction.getResultsClear());
    };
  }, []);

  useEffect(() => {
    setIsResultsReady(false);
    resultsFetchStartedRef.current = false;
  }, [patientId]);

  useEffect(() => {
    if (loading.status) {
      resultsFetchStartedRef.current = true;
      return;
    }

    if (loading.progress === 100) {
      setIsResultsReady(true);
      return;
    }

    if (resultsFetchStartedRef.current) {
      setIsResultsReady(true);
    }
  }, [loading.status, loading.progress]);

  useEffect(() => {
    if (!loading.status && visibleTestResults.length > 0) {
      setIsResultsReady(true);
    }
  }, [loading.status, visibleTestResults.length]);

  // Dispatches the next page of test history when more records exist on the server.
  const loadMoreResults = useCallback(() => {
    if (loading.status || !hasMoreResults || !patientId) {
      return;
    }

    dispatch(
      resultsAction.getResultsAction(String(patientId), {
        first: TEST_HISTORY_PAGE_SIZE,
        skip: results.skip + results.first,
        letter: '',
        testResultList: results.testResultList,
      }),
    );
  }, [dispatch, hasMoreResults, loading.status, patientId, results.first, results.skip, results.testResultList]);

  // Fetches the next page when the current chunk has no renderable rows but more records exist.
  useEffect(() => {
    if (loading.status || visibleTestResults.length > 0 || !hasMoreResults || !patientId) {
      return;
    }

    loadMoreResults();
  }, [hasMoreResults, loadMoreResults, loading.status, patientId, visibleTestResults.length]);

  // Returns true when the scroll container is at or near the bottom edge.
  const isScrollContainerNearBottom = useCallback((element: HTMLElement) => {
    return element.scrollTop + element.clientHeight >= element.scrollHeight - TEST_HISTORY_SCROLL_SLACK_PX;
  }, []);

  // Loads the next page when the list is scrolled to (or already at) the bottom.
  const tryLoadMoreFromScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer || !isScrollContainerNearBottom(scrollContainer)) {
      return;
    }

    loadMoreResults();
  }, [isScrollContainerNearBottom, loadMoreResults]);

  useEffect(() => {
    if (loading.status) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      tryLoadMoreFromScroll();
    });

    return () => cancelAnimationFrame(frameId);
  }, [loading.status, visibleTestResults.length, results.testResultList.length, tryLoadMoreFromScroll]);

  const customDisabled = disabled || loading.status;

  // Fetches the next chunk of test history when the list is scrolled to (or near) the bottom.
  const handleBottomScroll = () => {
    tryLoadMoreFromScroll();
  };

  return (
    <div className={`test-history${loading.status ? ' test-history_loading' : ''}`}>
      <div className="test-history__header">
        <h3 className="page-heading small">{strings.doctorScreen.testHistory}</h3>
      </div>
      <div className="test-history__body">
        {visibleTestResults.length > 0 ? (
          <div
            ref={scrollContainerRef}
            className={`test-history__items scroll${customDisabled ? ' disabled' : ''}`}
            onScroll={handleBottomScroll}
          >
            {visibleTestResults.map((item) => (
              <TestHistoryResult
                key={item.testId}
                result={item.testResult}
                unitMeasure={unitMeasure}
                userType={userType}
              />
            ))}
          </div>
        ) : (
          isResultsReady &&
          !loading.status &&
          !hasMoreResults && (
            <div className="test-history__empty">
              <EmptyList isEmpty={true} value={strings.doctorScreen.emptyTestHistory} />
            </div>
          )
        )}
        <PageLoader visible={loading.status} variant={visibleTestResults.length > 0 ? 'overlay' : 'inline'} />
      </div>
    </div>
  );
};

export default TestHistory;
